/**
 * WDK API Client
 *
 * Client for calling the backend WDK endpoints to derive addresses
 * using Tether WDK (which runs server-side in Node.js).
 */

import type { NetworkType } from '../types';

export interface WdkApiConfig {
  baseUrl: string;
  timeout?: number;
}

export interface DeriveAddressResponse {
  success: boolean;
  address?: string;
  chain?: string;
  path?: string;
  network?: string;
  error?: string;
}

export interface DeriveAllAddressesResponse {
  success: boolean;
  addresses?: {
    ethereum: string | null;
    bitcoin: string | null;
    ton: string | null;
    tron: string | null;
    solana: string | null;
    spark: string | null;
  };
  network?: string;
  error?: string;
}

export interface ValidateSeedResponse {
  success: boolean;
  isValid?: boolean;
  wordCount?: number;
  error?: string;
}

export interface GenerateSeedResponse {
  success: boolean;
  seed?: string;
  wordCount?: number;
  error?: string;
}

/**
 * WDK API Client for server-side Tether WDK integration
 */
export class WdkApiClient {
  private config: Required<WdkApiConfig>;

  constructor(config: WdkApiConfig) {
    this.config = {
      baseUrl: config.baseUrl,
      timeout: config.timeout || 30000,
    };
  }

  /**
   * Generate a new BIP-39 seed phrase using Tether WDK
   */
  async generateSeed(): Promise<GenerateSeedResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/wallets/wdk/generate-seed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to generate seed',
      };
    }
  }

  /**
   * Validate a BIP-39 seed phrase
   */
  async validateSeed(seed: string): Promise<ValidateSeedResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/wallets/wdk/validate-seed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seed }),
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to validate seed',
      };
    }
  }

  /**
   * Derive address for a specific chain using Tether WDK
   */
  async deriveAddress(
    seed: string,
    chain: NetworkType,
    network: 'mainnet' | 'testnet' = 'testnet'
  ): Promise<DeriveAddressResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/wallets/wdk/derive-address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seed, chain, network }),
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to derive address',
      };
    }
  }

  /**
   * Derive addresses for all chains using Tether WDK
   */
  async deriveAllAddresses(
    seed: string,
    network: 'mainnet' | 'testnet' = 'testnet'
  ): Promise<DeriveAllAddressesResponse> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/wallets/wdk/derive-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ seed, network }),
      });

      return await response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to derive addresses',
      };
    }
  }
}

// Default API URL (can be overridden)
const DEFAULT_API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// Singleton instance
let wdkApiClient: WdkApiClient | null = null;

/**
 * Get or create the WDK API client instance
 */
export function getWdkApiClient(baseUrl?: string): WdkApiClient {
  if (!wdkApiClient || (baseUrl && wdkApiClient['config'].baseUrl !== baseUrl)) {
    wdkApiClient = new WdkApiClient({
      baseUrl: baseUrl || DEFAULT_API_URL,
    });
  }
  return wdkApiClient;
}
